import { useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CakeIcon from '@mui/icons-material/Cake';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import PublicIcon from '@mui/icons-material/Public';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { getCountryName } from '../../utils/countries';
import { getTimezoneLabel } from '../../utils/timezones';

function CompletionStep({ formData, mode, success, error }) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'en';

  const countryName = useMemo(() => {
    return getCountryName(formData.country, currentLanguage);
  }, [formData.country, currentLanguage]);

  const timezoneLabel = useMemo(() => {
    return getTimezoneLabel(formData.timezone);
  }, [formData.timezone]);

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t('onboarding.complete.successTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {t('onboarding.complete.successMessage')}
        </Typography>
        <CircularProgress size={24} sx={{ mt: 3 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('onboarding.complete.heading')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('onboarding.complete.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2}>
        {/* Personal Info Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('onboarding.complete.personalInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CakeIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.personal.birthYear')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formData.birthYear}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalInformationIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.personal.diagnosisYear')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formData.diabetesDiagnosisYear}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalInformationIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.personal.diabetesType')}:
                  </Typography>
                  <Chip
                    label={formData.diabetesType === 'type1'
                      ? t('onboarding.personal.type1')
                      : t('onboarding.personal.type2')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PublicIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.personal.country')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {countryName}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Preferences Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('onboarding.complete.preferences')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.preferences.timezone')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {timezoneLabel}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BloodtypeIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.preferences.glucoseUnit')}:
                  </Typography>
                  <Chip
                    label={formData.preferredUnit === 'mmol' ? 'mmol/L' : 'mg/dL'}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LanguageIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.preferences.language')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formData.language === 'en'
                      ? t('onboarding.preferences.english')
                      : t('onboarding.preferences.swedish')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit notice */}
      <Alert severity="info" sx={{ mt: 3 }}>
        {t('onboarding.complete.editNotice')}
      </Alert>
    </Box>
  );
}

export default CompletionStep;
